<template>
  <div class="min-h-screen bg-gray-50 pb-20">
    <Head title="Mi Suscripción" />

    <div class="max-w-[1400px] mx-auto px-6 py-12">
      <!-- HEADER -->
      <div class="flex items-center gap-4 mb-10">
        <div class="w-12 h-12 rounded-2xl bg-gray-900 flex items-center justify-center shadow-lg shadow-gray-200">
          <i class="fas fa-crown text-orange-500" />
        </div>
        <div>
          <h1 class="text-2xl font-black text-gray-900 tracking-tight leading-none">Mi Suscripción</h1>
          <p class="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mt-1">Gestión de membresía y facturación</p>
        </div>
      </div>

      <!-- ALERTAS DE EXPIRACIÓN -->
      <div v-if="isExpired" class="mb-10 animate-pulse">
        <div class="bg-rose-500/10 border border-rose-500/20 rounded-[32px] p-8 flex items-center justify-between shadow-2xl shadow-rose-500/5">
          <div class="flex items-center gap-6">
            <div class="w-14 h-14 rounded-2xl bg-rose-500 text-white flex items-center justify-center text-2xl shadow-lg shadow-rose-900/20">
              <i class="fas fa-exclamation-triangle" />
            </div>
            <div>
              <h3 class="text-xl font-black text-rose-500 tracking-tight leading-none">Tu suscripción ha vencido</h3>
              <p class="text-sm font-bold text-rose-500/60 mt-2">Renueva ahora para mantener el acceso a todas tus herramientas profesionales.</p>
            </div>
          </div>
          <Link href="/subscription/plans" class="px-8 py-4 bg-rose-500 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-rose-600 transition-all shadow-xl shadow-rose-900/20 active:scale-95">
            Renovar Ahora
          </Link>
        </div>
      </div>

      <div v-else-if="isExpiringSoon" class="mb-10">
        <div class="bg-amber-500/10 border border-amber-500/20 rounded-[32px] p-8 flex items-center justify-between shadow-2xl shadow-amber-500/5">
          <div class="flex items-center gap-6">
            <div class="w-14 h-14 rounded-2xl bg-amber-500 text-white flex items-center justify-center text-2xl shadow-lg shadow-amber-900/20">
              <i class="fas fa-clock" />
            </div>
            <div>
              <h3 class="text-xl font-black text-amber-500 tracking-tight leading-none">Vence en {{ daysRemaining }} {{ daysRemaining === 1 ? 'día' : 'días' }}</h3>
              <p class="text-sm font-bold text-amber-500/60 mt-2">Evita interrupciones en tu servicio renovando antes del {{ formatDate(account.plan_expires_at) }}.</p>
            </div>
          </div>
          <Link href="/subscription/plans" class="px-8 py-4 bg-amber-500 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-amber-600 transition-all shadow-xl shadow-amber-900/20 active:scale-95">
            Renovar Prime
          </Link>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <!-- ══ CARD DE PLAN HERO (12/12) ══ -->
        <div class="lg:col-span-12">
          <div class="relative overflow-hidden rounded-[32px] bg-gray-900 p-10 shadow-2xl border border-white/5 group">
            <!-- Decoración -->
            <div class="absolute -right-20 -top-20 w-80 h-80 bg-orange-600/10 rounded-full blur-3xl group-hover:scale-110 transition-transform duration-700" />
                        
            <div class="relative z-10 flex flex-col md:flex-row justify-between items-center gap-8">
              <div class="flex items-center gap-8">
                <div class="w-24 h-24 rounded-[32px] bg-white/5 backdrop-blur-xl border border-white/10 flex items-center justify-center text-4xl shadow-2xl">
                  <i class="fas fa-gem text-orange-500 drop-shadow-[0_0_15px_rgba(249,115,22,0.5)]" />
                </div>
                <div>
                  <h4 class="text-xs font-black text-orange-500 uppercase tracking-[0.4em] mb-2">Tu Plan Actual</h4>
                  <h2 class="text-5xl font-black text-white tracking-tighter">{{ account.plan_name }}</h2>
                  <div class="flex items-center gap-3 mt-4">
                    <span v-if="account.has_active_subscription" class="px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-green-500/10 text-green-400 border border-green-500/20">
                      Suscripción Activa
                    </span>
                    <span v-else class="px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-amber-500/10 text-amber-400 border border-amber-500/20">
                      Pendiente de Renovación
                    </span>
                    <p class="text-white/40 text-sm font-medium tracking-tight">Precio: <span class="text-white font-bold">${{ account.plan_price }}/mes</span></p>
                  </div>
                </div>
              </div>

              <div class="flex gap-3">
                <Link
                  href="/subscription/plans" 
                  class="h-14 px-10 bg-orange-500 text-white rounded-2xl hover:bg-orange-600 transition-all font-black text-xs uppercase tracking-widest flex items-center gap-3 shadow-xl shadow-orange-900/20 active:scale-95"
                >
                  <i class="fas fa-exchange-alt text-[10px]" /> Cambiar Plan
                </Link>
                <form v-if="!account.is_on_free_plan" class="inline" @submit.prevent="cancelSubscription">
                  <button
                    type="submit" 
                    class="h-14 px-8 bg-white/5 border border-white/10 text-white rounded-2xl hover:bg-rose-500 hover:border-rose-500 transition-all font-bold text-xs uppercase tracking-widest active:scale-95"
                  >
                    <i class="fas fa-times-circle mr-2 opacity-50" /> Cancelar
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        <!-- ══ SECCIÓN IZQUIERDA: CARACTERÍSTICAS (7/12) ══ -->
        <div class="lg:col-span-7 flex flex-col gap-8">
          <div class="bg-white rounded-[32px] p-10 border border-gray-100 shadow-sm min-h-[400px]">
            <h3 class="text-xl font-black text-gray-900 tracking-tight mb-8 flex items-center gap-3">
              <i class="fas fa-check-circle text-orange-500" /> Características Incluidas
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div
                v-for="feature in account.plan_features" :key="feature" 
                class="flex items-center gap-4 p-4 rounded-2xl bg-gray-50 border border-gray-100 hover:border-orange-200 transition-colors group"
              >
                <div class="w-8 h-8 rounded-xl bg-orange-500 text-white flex items-center justify-center text-xs shadow-lg shadow-orange-100 group-hover:scale-110 transition-transform">
                  <i class="fas fa-check" />
                </div>
                <span class="text-sm font-bold text-gray-600">{{ feature }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- ══ SECCIÓN DERECHA: FACTURACIÓN & STATUS (5/12) ══ -->
        <div class="lg:col-span-5 flex flex-col gap-8">
          <!-- Card de Próximo Pago / Expiración -->
          <div class="bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm relative overflow-hidden">
            <div class="absolute -right-10 -bottom-10 w-40 h-40 bg-gray-50 rounded-full blur-2xl opacity-50" />
            <p class="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4 leading-none">Estado de Cuenta</p>
            <div class="flex items-center justify-between mb-8">
              <div v-if="account.has_active_subscription">
                <h4 class="text-[10px] font-bold text-green-600 uppercase tracking-widest mb-1">Próxima renovación</h4>
                <p class="text-2xl font-black text-gray-900 tracking-tight">{{ formatDate(account.plan_expires_at) }}</p>
              </div>
              <div v-else>
                <h4 class="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-1">Estatus</h4>
                <p class="text-2xl font-black text-gray-900 tracking-tight">Sin suscripción activa</p>
              </div>
              <div class="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-400 border border-gray-100">
                <i class="fas fa-calendar-alt" />
              </div>
            </div>

            <!-- Info Detallada (Si existe suscripción) -->
            <div v-if="subscription" class="space-y-4 pt-6 border-t border-gray-50">
              <div class="flex justify-between items-center text-sm">
                <span class="font-bold text-gray-400">ID Suscripción</span>
                <span class="font-mono text-gray-900 font-black">#{{ subscription.id }}</span>
              </div>
              <div class="flex justify-between items-center text-sm">
                <span class="font-bold text-gray-400">Estado Facturación</span>
                <span class="px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-[10px] font-black uppercase tracking-widest border border-blue-100">
                  {{ subscription.status }}
                </span>
              </div>
            </div>
          </div>
                    
          <!-- Card de Soporte o Ayuda -->
          <div class="bg-orange-50 rounded-[32px] p-8 border border-orange-100 shadow-sm group">
            <div class="flex items-center gap-4 mb-4">
              <div class="w-10 h-10 rounded-xl bg-orange-500 text-white flex items-center justify-center shadow-lg shadow-orange-200 group-hover:rotate-12 transition-transform">
                <i class="fas fa-headset" />
              </div>
              <h4 class="text-sm font-black text-orange-950 uppercase tracking-tight">Soporte Prioritario</h4>
            </div>
            <p class="text-xs font-bold text-orange-800/70 leading-relaxed mb-6">¿Tienes dudas con tu facturación o quieres cambiar a un plan empresarial? Nuestro equipo está listo para ayudarte.</p>
            <a href="https://wa.me/584244010195" target="_blank" class="w-full h-12 px-6 bg-white text-orange-600 rounded-xl flex items-center justify-center font-black text-[10px] uppercase tracking-widest shadow-sm hover:shadow-md transition-all active:scale-95">
              Contactar Asesor
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'

export default defineComponent({
  components: {
    Layout,
    Link,
    Head,
  },

  layout: Layout,

  props: {
    account: Object,
    subscription: Object,
  },

  computed: {
    isExpiringSoon() {
      if (!this.account.plan_expires_at || this.account.is_on_free_plan) return false
      const remaining = this.daysRemaining
      return remaining >= 0 && remaining <= 5
    },
    isExpired() {
      if (!this.account.plan_expires_at || this.account.is_on_free_plan) return false
      return this.daysRemaining < 0
    },
    daysRemaining() {
      if (!this.account.plan_expires_at) return 0
      const expiresAt = new Date(this.account.plan_expires_at)
      const today = new Date()
      const diffTime = expiresAt - today
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    },
  },

  methods: {
    formatDate(date) {
      if (!date) return 'Sin fecha'
      return new Date(date).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    },

    cancelSubscription() {
      if (confirm('¿Estás seguro de que deseas cancelar tu suscripción?')) {
        this.$inertia.post(route('subscriptions.cancel'))
      }
    },
  },
})
</script> 